import React from "react";
import { CSButton } from "@cadenza/ui";
import { FaUnlink, FaLink, FaRegEyeSlash, FaRegEye } from "react-icons/fa";
import {
  BsArrow90DegLeft,
  BsArrowReturnLeft,
  BsArrow90DegRight,
  BsArrowReturnRight,
} from "react-icons/bs";

// TO DO: add a button to show/hide loop lines for looped threads
// Hiden lines should show the loop to state at the end of the thread.
const ComponentForm = ({
  editComponent,
  onChange,
  onKeyDown,
  onSubthreadAction,
  onThreadAction,
  onLabelReset,
}) => {
  if (!editComponent) return null;

  // Helper function to render label reset button
  const renderLabelReset = () => {
    const type =
      editComponent.class === "activity"
        ? "label"
        : editComponent.type === "condition"
          ? "condition-label"
          : editComponent.type === "repeat"
            ? "repeat-label"
            : null;

    return (
      type && (
        <CSButton
          onClick={() => {
            onLabelReset(type);
          }}
          title="Reset label position to default"
          aria-label="Reset label position to default"
        >
          Reset label position
        </CSButton>
      )
    );
  };

  // Helper function to render merge-related buttons
  const renderMergeButtons = () => {
    const isMerged = editComponent.status === "merged";
    const buttons = [
      {
        key: "merge-toggle",
        onClick: () =>
          onThreadAction({
            action: isMerged ? "unmerge" : "merge",
            editComponent,
          }),
        title: isMerged ? "Unmerge" : "Merge",
        ariaLabel: isMerged
          ? "Unmerge the selected thread"
          : "Merge the selected thread",
        icon: isMerged ? (
          <BsArrowReturnRight className="cs-icon" title="Unmerge this thread" />
        ) : (
          <BsArrow90DegRight className="cs-icon" title="Merge this thread" />
        ),
      },
    ];

    return buttons.map(({ key, onClick, title, ariaLabel, icon }) => (
      <CSButton
        key={key}
        onClick={onClick}
        title={title}
        aria-label={ariaLabel}
      >
        {icon}
      </CSButton>
    ));
  };

  // Helper function to render loop-related buttons
  const renderLoopButtons = () => {
    const isLooped = editComponent.status === "looped";

    const buttons = [
      {
        key: "loop-toggle",
        onClick: () =>
          onSubthreadAction({
            action: isLooped ? "unloop" : "loop",
            editComponent,
          }),
        title: isLooped ? "Unloop" : "Loop",
        ariaLabel: isLooped
          ? "Unloop the selected thread"
          : "Loop the selected thread",
        icon: isLooped ? (
          <BsArrowReturnLeft className="cs-icon" title="Unloop this thread" />
        ) : (
          <BsArrow90DegLeft className="cs-icon" title="Loop this thread" />
        ),
      },
    ];

    // Add show/hide button when looped
    if (isLooped) {
      // Default to true (show) if showLoop property doesn't exist on existing objects
      const showLoop =
        editComponent.showLoop !== undefined ? editComponent.showLoop : true;
      buttons.unshift({
        key: "show-hide",
        onClick: () =>
          onSubthreadAction({
            action: showLoop ? "hideLoop" : "showLoop",
            editComponent,
          }),
        title: showLoop ? "Hide loop lines" : "Show loop lines",
        ariaLabel: showLoop ? "Hide loop lines" : "Show loop lines",
        icon: showLoop ? (
          <FaRegEyeSlash className="cs-icon" title="Hide loop lines" />
        ) : (
          <FaRegEye className="cs-icon" title="Show loop lines" />
        ),
      });
    }

    return buttons.map(({ key, onClick, title, ariaLabel, icon }) => (
      <CSButton
        key={key}
        onClick={onClick}
        title={title}
        aria-label={ariaLabel}
      >
        {icon}
      </CSButton>
    ));
  };

  const renderField = () => {
    switch (editComponent.class) {
      case "role":
        return (
          <>
            <label>
              Label position:
              <select
                name="labelPosition"
                value={editComponent.labelPosition || "above"}
                onChange={onChange}
              >
                <option value="above">Above</option>
                <option value="below">Below</option>
              </select>
            </label>
            <br />
            <label>
              Transient:
              <input
                type="checkbox"
                name="transient"
                checked={!!editComponent.transient}
                onChange={onChange}
              />
            </label>
            <br />
            <label>
              Multiple:
              <input
                type="checkbox"
                name="multiple"
                checked={editComponent.multiple}
                onChange={onChange}
              />
            </label>
          </>
        );
      case "activity": {
        return (
          <>
            {editComponent.type === "interaction" && (
              <>
                <label>
                  Initiator:
                  <input
                    type="checkbox"
                    name="initiator"
                    checked={!!editComponent.initiator}
                    onChange={onChange}
                  />
                </label>
                <br />
              </>
            )}
          </>
        );
      }

      case "state":
        return (
          <label>
            Show in RAD:
            <input
              type="checkbox"
              name="show"
              checked={!!editComponent.show}
              onChange={onChange}
            />
          </label>
        );

      case "thread":
        return (
          <label>
            Manage thread merging:
            <div className="button-container">{renderMergeButtons()}</div>
          </label>
        );

      case "refinement":
      case "case":
      case "part":
        switch (editComponent.type) {
          case "case":
          case "condition":
          case "part":
          case "partThread":
            return (
              <>
                <label>
                  Manage refinement:
                  <div className="button-container">
                    <CSButton
                      onClick={() => {
                        onSubthreadAction({
                          action: "insertAtStart",
                          editComponent,
                        });
                      }}
                      title="Add at beginning"
                      aria-label="Add a refinement thread at beginning"
                    >
                      ⤶ +
                    </CSButton>
                    {!(
                      editComponent.type === "case" ||
                      editComponent.type === "part"
                    ) && (
                      <CSButton
                        onClick={() => {
                          onSubthreadAction({
                            action: "insertAfter",
                            editComponent,
                          });
                        }}
                        title="Insert after selection"
                        aria-label="Insert a refinement thread after the selected thread"
                      >
                        ↴
                      </CSButton>
                    )}
                    <CSButton
                      onClick={() => {
                        onSubthreadAction({
                          action: "addAtEnd",
                          editComponent,
                        });
                      }}
                      title="Add at end"
                      aria-label="Add a refinement thread at end"
                    >
                      + ⤷
                    </CSButton>
                  </div>
                </label>
                {!(
                  editComponent.type === "case" || editComponent.type === "part"
                ) && (
                  <label>
                    Manage subthread:
                    <div className="button-container">
                      {editComponent.status !== "detached" && (
                        <CSButton
                          onClick={() => {
                            onSubthreadAction({
                              action: "detach",
                              editComponent,
                            });
                          }}
                          title="Detach"
                          aria-label="Detach the selected thread from the main sequence"
                        >
                          <FaUnlink
                            className="cs-icon"
                            title="Detach this thread from the main sequence"
                          />
                        </CSButton>
                      )}
                      {editComponent.status === "detached" && (
                        <CSButton
                          onClick={() => {
                            onSubthreadAction({
                              action: "attach",
                              editComponent,
                            });
                          }}
                          title="Attach"
                          aria-label="Re-attach the selected thread to the main sequence"
                        >
                          <FaLink
                            className="cs-icon"
                            title="Re-attach this thread to the main sequence"
                          />
                        </CSButton>
                      )}
                      {renderLoopButtons()}
                      <CSButton
                        onClick={() => {
                          onSubthreadAction({
                            action: "shiftLeft",
                            editComponent,
                          });
                        }}
                        title="Shift left"
                        aria-label="Shift the selected thread to the left"
                      >
                        ⬅
                      </CSButton>
                      <CSButton
                        onClick={() => {
                          onSubthreadAction({
                            action: "shiftRight",
                            editComponent,
                          });
                        }}
                        title="Shift right"
                        aria-label="Shift the selected thread to the right"
                      >
                        ➡
                      </CSButton>
                    </div>
                  </label>
                )}
              </>
            );
          default:
            return null;
        }
      default:
        return null;
    }
  };

  return (
    <>
      <label>
        Label:
        <input
          type="text"
          name="label"
          value={editComponent.label || ""}
          placeholder="Enter a new value..."
          onChange={onChange}
          pattern="[ a-zA-Z0-9_\-]+"
          onKeyDown={onKeyDown}
          disabled={!editComponent}
        />
      </label>
      {renderLabelReset()}
      <hr />
      {renderField()}
      {/* <div>
        <hr />
        <label>
          Position:
          <input
            type="text"
            name="id"
            value={
              editComponent.position
                ? editComponent.position.x + ", " + editComponent.position.y
                : ""
            }
            disabled
          />
        </label>
        {editComponent.absPos && (
          <label>
            Abs Position:
            <input
              type="text"
              name="id"
              value={
                editComponent.position
                  ? editComponent.absPos.x + ", " + editComponent.absPos.y
                  : ""
              }
              disabled
            />
          </label>
        )}
      </div> */}
    </>
  );
};

export default ComponentForm;
